import useRegisterPin from '@hooks/native/useRegisterPin';
import {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface PinContextProps {
    pin: string;
    setPin: (pin: string) => void;
    useBio: boolean;
    setUseBio: Dispatch<SetStateAction<boolean>>;
    isCheck: boolean;
    /** 확인 입력이 첫 입력과 달라 처음부터 다시 받는 중인지 */
    isMismatch: boolean;
    /** 입력이 끝난 PIN과 생체 인증 체크 여부를 넘겨준다 */
    onPinChanged?: (pin: string, useBio: boolean) => void;
}

const MAX_LENGTH = 6;

export const PinContext = createContext<PinContextProps>({} as PinContextProps);

interface PinContextProviderProps extends Pick<PinContextProps, 'onPinChanged'>{
    children?: ReactNode;
    /** false면 확인 입력 없이 한 번만 입력받는다 */
    confirm?: boolean;
}

export function PinContextProvider({ children, onPinChanged, confirm = true }: PinContextProviderProps) {
    const { t } = useTranslation('easyCard');
    const [pin, setPinValue] = useState<string>('');
    const [useBio, setUseBio] = useState<boolean>(false);
    const [isCheck, setIsCheck] = useState<boolean>(false);
    const [firstPin, setFirstPin] = useState<string>('');
    const [isMismatch, setIsMismatch] = useState<boolean>(false);

    const { registerPin } = useRegisterPin({
        onSuccess() {
            toast.success(t('pin.bio.registered'));
        },
        onError(status) {
            toast.error(t('pin.bio.registerError', { status }));
        },
    });

    // 6자리가 채워지는 입력에서 한 번만 처리한다 (첫 입력 → 확인 입력 → 등록).
    // 확인 입력이 필요 없으면 첫 입력에서 바로 넘긴다
    const setPin = (next: string) => {
        setPinValue(next);
        // 불일치 안내는 다시 입력을 시작하면 지운다
        if (next.length > 0) {
            setIsMismatch(false);
        }
        if (next.length !== MAX_LENGTH || next === pin) {
            return;
        }
        if (!confirm) {
            onPinChanged?.(next, useBio);
            return;
        }
        if (!isCheck) {
            // 마지막 자리가 채워진 것을 잠깐 보여준 뒤 확인 입력으로 넘어간다
            setTimeout(() => {
                setFirstPin(next);
                setIsCheck(true);
                setPinValue('');
            }, 200);
            return;
        }
        if (next !== firstPin) {
            setIsMismatch(true);
            setPinValue('');
            setFirstPin('');
            setIsCheck(false);
            return;
        }
        onPinChanged?.(next, useBio);
        if (useBio) {
            registerPin(next);
        }
    };

    const contextValue = useMemo<PinContextProps>(() => ({
        pin,
        setPin,
        useBio,
        setUseBio,
        isCheck,
        isMismatch,
        onPinChanged,
    }), [pin, useBio, isCheck, isMismatch, firstPin, onPinChanged, confirm]);

    return (
        <PinContext value={contextValue}>
            {children}
        </PinContext>
    );
}
